// src/components/modals/ViewRecipeModal.jsx
import { X } from 'lucide-react'
import PropTypes from 'prop-types'

const ViewRecipeModal = ({ isOpen, onClose, recipe }) => {
  if (!isOpen || !recipe) return null

  // Format cost for display
  const formatCost = (cost) => {
    if (typeof cost === 'number') {
      return cost.toFixed(2)
    }
    return '0.00'
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recipe Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Recipe Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipe Name *
              </label>
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {recipe.name || 'Unnamed Recipe'}
              </div>
            </div>

            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product name *
              </label>
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {recipe.product?.name || 'Unknown Product'}
              </div>
            </div>
          </div>

          {/* Cost */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cost (Rs) *
            </label>
            <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
              {formatCost(recipe.cost)}
            </div>
          </div>

          {/* Raw Material List */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raw Material List *
            </label>
            
            {recipe.rawMaterialList && recipe.rawMaterialList.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm font-medium text-gray-700 px-3 py-2 bg-gray-100 rounded-lg">
                  <div>Raw material</div>
                  <div>Qty usage</div>
                </div>
                
                {recipe.rawMaterialList.map((item, index) => (
                  <div key={index} className="grid grid-cols-2 gap-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="text-sm text-gray-900">
                      {item.rawMaterial?.name || 'Unknown Material'}
                    </div>
                    <div className="text-sm text-gray-900">
                      {item.quantity || 0} {item.rawMaterial?.unit?.abbreviation || 'unit'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No raw materials configured for this recipe
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-2 gap-6 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <div className="text-sm text-gray-600">
                {formatDate(recipe.createdAt)}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Updated
              </label>
              <div className="text-sm text-gray-600">
                {formatDate(recipe.updatedAt)}
              </div>
            </div>
          </div>

          {recipe.createdBy && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created By
              </label>
              <div className="text-sm text-gray-600">
                {recipe.createdBy.username || 'Unknown User'}
              </div>
            </div>
          )}

          {/* Status */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              recipe.isActive !== false 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {recipe.isActive !== false ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

ViewRecipeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipe: PropTypes.object
}

export default ViewRecipeModal